type QueryCacheEntry<T> = {
  data?: T;
  expiresAt?: number;
  promise?: Promise<T>;
  controller?: AbortController;
};

type QueryCacheFetchOptions<T> = {
  key: string;
  ttlMs?: number;
  force?: boolean;
  request: (signal: AbortSignal) => Promise<T>;
};

class QueryCache {
  private entries = new Map<string, QueryCacheEntry<unknown>>();

  fetch<T>({ key, ttlMs = 0, force = false, request }: QueryCacheFetchOptions<T>): Promise<T> {
    const now = Date.now();
    const existing = this.entries.get(key) as QueryCacheEntry<T> | undefined;

    if (!force && existing?.data !== undefined && (existing.expiresAt ?? 0) > now) {
      return Promise.resolve(existing.data);
    }

    if (!force && existing?.promise) {
      return existing.promise;
    }

    if (force) {
      this.cancel(key);
    }

    const controller = new AbortController();
    const promise = request(controller.signal)
      .then((data) => {
        // A request cancelled/replaced during invalidation must never repopulate
        // the cache, even when its transport ignores AbortSignal.
        if (this.entries.get(key)?.promise === promise && !controller.signal.aborted) {
          this.entries.set(key, { data, expiresAt: Date.now() + ttlMs });
        }
        return data;
      })
      .catch((error) => {
        const current = this.entries.get(key) as QueryCacheEntry<T> | undefined;
        if (current?.promise === promise) {
          this.entries.delete(key);
        }
        throw error;
      });

    this.entries.set(key, {
      ...(existing?.data !== undefined ? { data: existing.data, expiresAt: existing.expiresAt } : {}),
      promise,
      controller,
    });

    return promise;
  }

  invalidate(key: string): void {
    this.cancel(key);
    this.entries.delete(key);
  }

  invalidatePrefix(prefix: string): void {
    this.entries.forEach((_entry, key) => {
      if (key.startsWith(prefix)) {
        this.invalidate(key);
      }
    });
  }

  cancel(key: string): void {
    const entry = this.entries.get(key);
    entry?.controller?.abort();
  }

  clear(): void {
    this.entries.forEach((_entry, key) => {
      this.cancel(key);
    });
    this.entries.clear();
  }
}

export const queryCache = new QueryCache();
